import React, { useState } from 'react';
import { Check, Trash2, Database, AlertTriangle } from 'lucide-react';
import { createLocation } from '../../services/locations';
import { createEquipment } from '../../services/equipment';
import { createTalent } from '../../services/talent';
import { getAllLocations, deleteLocation } from '../../services/locations';
import { getAllEquipment, deleteEquipment } from '../../services/equipment';
import { getAllTalent, deleteTalent } from '../../services/talent';
import { getAllBookings, deleteBooking } from '../../services/bookings';
import './TestData.css';

// Sample test data
const testLocations = [
  {
    name: 'Downtown Loft Studio',
    address: '123 Michigan Ave',
    city: 'Chicago',
    state: 'IL',
    zipCode: '60601',
    size: '3,500 sq ft',
    capacity: '40 people',
    price: '1500',
    description: 'Modern industrial loft with exposed brick, 18ft ceilings, and floor-to-ceiling windows. Perfect for fashion shoots, music videos, and commercial productions.',
    amenities: ['WiFi', 'Parking', 'Green Room', 'Kitchen', 'Climate Control', 'Loading Dock'],
    images: ['https://images.unsplash.com/photo-1497366216548-37526070297c?w=800']
  },
  {
    name: 'Rooftop Skyline View',
    address: '456 State St',
    city: 'Chicago',
    state: 'IL',
    zipCode: '60605',
    size: '5,000 sq ft',
    capacity: '75 people',
    price: '2500',
    description: 'Stunning rooftop venue with panoramic Chicago skyline views. Ideal for golden hour shoots, commercials, and special events.',
    amenities: ['City Views', 'Elevator Access', 'Restrooms', 'Power Outlets', 'Shade Structures'],
    images: ['https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800']
  },
  {
    name: 'Industrial Warehouse',
    address: '789 Fulton Market',
    city: 'Chicago',
    state: 'IL',
    zipCode: '60607',
    size: '8,000 sq ft',
    capacity: '100 people',
    price: '2000',
    description: 'Raw industrial space with concrete floors, metal beams, and versatile open floor plan. Great for car shoots, large-scale productions, and events.',
    amenities: ['Drive-In Access', 'High Ceilings', 'Natural Light', 'Blackout Capable', 'Generator Ready'],
    images: ['https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=800']
  }
];

const testEquipment = [
  {
    name: 'Sony FX6 Cinema Camera',
    brand: 'Sony',
    model: 'FX6',
    category: 'Camera',
    condition: 'excellent',
    quantity: '2',
    price: '350',
    description: 'Full-frame cinema camera with 4K 120fps, dual base ISO, and professional features.',
    image: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800',
    available: true
  },
  {
    name: 'ARRI SkyPanel S60-C',
    brand: 'ARRI',
    model: 'SkyPanel S60-C',
    category: 'Lighting',
    condition: 'excellent',
    quantity: '4',
    price: '200',
    description: 'Professional LED soft light with full RGB color control and DMX.',
    image: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800',
    available: true
  },
  {
    name: 'Sennheiser MKH 416',
    brand: 'Sennheiser',
    model: 'MKH 416',
    category: 'Audio',
    condition: 'excellent',
    quantity: '3',
    price: '75',
    description: 'Industry-standard shotgun microphone for film and broadcast.',
    image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800',
    available: true
  },
  {
    name: 'Canon 70-200mm f/2.8L III',
    brand: 'Canon',
    model: '70-200mm f/2.8L III',
    category: 'Lens',
    condition: 'excellent',
    quantity: '2',
    price: '85',
    description: 'Professional telephoto zoom lens with fast f/2.8 aperture.',
    image: 'https://images.unsplash.com/photo-1617005082133-548c4dd27f35?w=800',
    available: true
  },
  {
    name: 'DJI Ronin RS3 Pro',
    brand: 'DJI',
    model: 'Ronin RS3 Pro',
    category: 'Grip',
    condition: 'excellent',
    quantity: '3',
    price: '125',
    description: 'Professional 3-axis gimbal stabilizer for cinema cameras.',
    image: 'https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=800',
    available: true
  }
];

const testTalent = [
  {
    name: 'Marcus Chen',
    category: 'Director of Photography',
    experience: '12+ years',
    rate: '800',
    description: 'Award-winning DP with experience in commercials, music videos, and feature films. Known for cinematic lighting and dynamic camera work.',
    skills: ['Cinematography', 'Lighting Design', 'Color Grading', 'Aerial Cinematography'],
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800',
    available: true
  },
  {
    name: 'Sarah Rodriguez',
    category: 'Director',
    experience: '10+ years',
    rate: '1000',
    description: 'Creative director specializing in brand storytelling and commercial content. Portfolio includes work for Fortune 500 companies.',
    skills: ['Commercial Direction', 'Brand Strategy', 'Narrative Storytelling', 'Creative Development'],
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800',
    available: true
  },
  {
    name: 'David Thompson',
    category: 'Gaffer',
    experience: '15+ years',
    rate: '650',
    description: 'Expert lighting technician with extensive experience in studio and location work. Union member with major film credits.',
    skills: ['Studio Lighting', 'Location Lighting', 'Electrical', 'Rigging'],
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=800',
    available: true
  },
  {
    name: 'Emily Park',
    category: 'Producer',
    experience: '8+ years',
    rate: '750',
    description: 'Organized and detail-oriented producer with a track record of delivering projects on time and under budget.',
    skills: ['Production Management', 'Budgeting', 'Scheduling', 'Client Relations'],
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=800',
    available: true
  }
];

const TestData = () => {
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState({});

  const seedData = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    setStatus(prev => ({ ...prev, [type]: 'Seeding...' }));

    try {
      let data, createFn;

      switch (type) {
        case 'locations':
          data = testLocations;
          createFn = createLocation;
          break;
        case 'equipment':
          data = testEquipment;
          createFn = createEquipment;
          break;
        case 'talent':
          data = testTalent;
          createFn = createTalent;
          break;
        default:
          return;
      }

      let successCount = 0;
      for (const item of data) {
        const result = await createFn(item);
        if (result.success) successCount++;
      }

      setStatus(prev => ({
        ...prev,
        [type]: `Success! Added ${successCount} ${type}`
      }));
    } catch (error) {
      setStatus(prev => ({ ...prev, [type]: `Error: ${error.message}` }));
    }

    setLoading(prev => ({ ...prev, [type]: false }));
  };

  const clearData = async (type) => {
    if (!window.confirm(`Are you sure you want to delete ALL ${type}? This cannot be undone!`)) {
      return;
    }

    setLoading(prev => ({ ...prev, [`clear_${type}`]: true }));
    setStatus(prev => ({ ...prev, [type]: 'Clearing...' }));

    try {
      let getAllFn, deleteFn;

      switch (type) {
        case 'locations':
          getAllFn = getAllLocations;
          deleteFn = deleteLocation;
          break;
        case 'equipment':
          getAllFn = getAllEquipment;
          deleteFn = deleteEquipment;
          break;
        case 'talent':
          getAllFn = getAllTalent;
          deleteFn = deleteTalent;
          break;
        case 'bookings':
          getAllFn = getAllBookings;
          deleteFn = deleteBooking;
          break;
        default:
          return;
      }

      const result = await getAllFn();
      if (result.success) {
        let deleteCount = 0;
        for (const item of result.data) {
          const delResult = await deleteFn(item.id);
          if (delResult.success) deleteCount++;
        }
        setStatus(prev => ({
          ...prev,
          [type]: `Cleared ${deleteCount} ${type}`
        }));
      }
    } catch (error) {
      setStatus(prev => ({ ...prev, [type]: `Error: ${error.message}` }));
    }

    setLoading(prev => ({ ...prev, [`clear_${type}`]: false }));
  };

  const DataSection = ({ title, type, canSeed = true }) => (
    <div className="test-data-section">
      <h3>{title}</h3>
      <div className="test-data-actions">
        {canSeed && (
          <button
            onClick={() => seedData(type)}
            disabled={loading[type]}
            className="btn btn-primary"
          >
            <Database size={16} />
            {loading[type] ? 'Seeding...' : `Seed ${title}`}
          </button>
        )}
        <button
          onClick={() => clearData(type)}
          disabled={loading[`clear_${type}`]}
          className="btn btn-danger"
        >
          <Trash2 size={16} />
          {loading[`clear_${type}`] ? 'Clearing...' : `Clear All ${title}`}
        </button>
      </div>
      {status[type] && (
        <div className={`test-data-status ${status[type].includes('Error') ? 'error' : 'success'}`}>
          {status[type].includes('Error') ? <AlertTriangle size={16} /> : <Check size={16} />}
          {status[type]}
        </div>
      )}
    </div>
  );

  return (
    <div className="test-data-page">
      <div className="page-header">
        <h1 className="page-title">Test Data Manager</h1>
        <p className="page-subtitle">
          Seed sample data for testing or clear all data from collections
        </p>
      </div>

      <div className="test-data-info-safe">
        <Check size={24} />
        <div>
          <strong>Safe to use:</strong> Seeding ADDS new items to your existing data - it will NOT overwrite or delete anything.
          Only use "Clear All" if you want to remove ALL items from a collection.
        </div>
      </div>

      <div className="test-data-warning">
        <AlertTriangle size={24} />
        <div>
          <strong>Danger Zone:</strong> The "Clear All" buttons will permanently delete ALL items in that collection.
          Your existing locations and equipment are safe unless you click Clear.
        </div>
      </div>

      <div className="test-data-grid">
        <DataSection title="Locations" type="locations" />
        <DataSection title="Equipment" type="equipment" />
        <DataSection title="Talent" type="talent" />
        <DataSection title="Bookings" type="bookings" canSeed={false} />
      </div>

      <div className="test-data-info">
        <h3>What Seeding Does:</h3>
        <ul>
          <li><strong>Locations:</strong> Adds {testLocations.length} sample locations (loft, rooftop, warehouse) - use only if you need test data</li>
          <li><strong>Equipment:</strong> Adds {testEquipment.length} sample items (camera, lights, audio, lens, gimbal) - use only if you need test data</li>
          <li><strong>Talent:</strong> Adds {testTalent.length} sample crew members (DP, Director, Gaffer, Producer) - useful for testing</li>
        </ul>
        <p style={{marginTop: '12px', color: 'var(--color-text-muted)', fontSize: '14px'}}>
          Note: If you already have real data, you probably don't need to seed. This is mainly for testing the booking flow.
        </p>
      </div>
    </div>
  );
};

export default TestData;
